import { SphereAvatar } from "@components/header/sphere-avatar";
import { SdbMemberTypes } from "@custom-types/member-types";
import { Box } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { blue, neutral, TeamIcon } from "@flat-ui";
import { withEllipsis } from "@styles/common-styles";
import { getMailToLink } from "@utils/email-utils";
import { isTeam } from "@utils/team-utils";
import {
  getPendingUserIcon,
  getPrettyName,
  getUserInitials,
  isMemberActive,
} from "@utils/user-utils";

interface Props {
  /** The member to show the details in the title */
  member?: SdbMemberTypes | FaroCoreApiTypes.UserAsManager | null;

  /** If the member is the current user */
  isCurrentUser: boolean;
}

/** Width in pixels for the name and email wrapper, needed to show ellipsis */
const NAME_EMAIL_WRAPPER_WIDTH = "165px";

/**
 * Component that shows the title of the menu when the user hovers over a member to see a quick
 * overlook about the user and open the profile page.
 * Includes the avatar, name and email.
 */
export function UserMoreInfoMenuTitle({
  member,
  isCurrentUser,
}: Props): JSX.Element {
  if (!member) {
    // eslint-disable-next-line react/jsx-no-useless-fragment -- we need as empty component.
    return <></>;
  }

  return (
    <Box
      component="div"
      sx={{
        height: "83px",
        width: "100%",
        backgroundColor: neutral[50],
        display: "flex",
        alignItems: "center",
        padding: "11px",
      }}
    >
      {/* Wrapper for avatar */}
      <Box
        component="div"
        sx={{
          marginRight: "16px",
        }}
      >
        {isTeam(member) ? (
          <SphereAvatar
            icon={<TeamIcon sx={{ width: "20px", height: "20px" }} />}
            size="small"
            shouldHideWhiteRim={true}
            backgroundColor={neutral[700]}
          />
        ) : (
          <SphereAvatar
            initials={getUserInitials(member)}
            src={member?.thumbnailUrl}
            icon={getPendingUserIcon(member, "large")}
            size="large"
            shouldUseSimplifiedAvatar={!isCurrentUser}
            shouldHideWhiteRim={true}
          />
        )}
      </Box>

      {/* Wrapper for name ane email */}
      <Box component="div">
        {/* Wrapper for name */}
        {isMemberActive(member) && (
          <Box
            component="div"
            sx={{
              fontSize: "12px",
              color: neutral[1000],
              width: NAME_EMAIL_WRAPPER_WIDTH,
              ...withEllipsis,
            }}
          >
            <var>{getPrettyName(member)}</var>
          </Box>
        )}

        {/* Wrapper for email */}
        {!isTeam(member) && (
          <Box
            component="div"
            sx={{
              fontSize: "10px",
            }}
          >
            <var>
              {getMailToLink(member?.email ?? "", {
                color: blue[500],
                // inline-block is needed to have ellipsis on a elements.
                display: "inline-block",
                width: NAME_EMAIL_WRAPPER_WIDTH,
                ...withEllipsis,
              })}
            </var>
          </Box>
        )}
      </Box>
    </Box>
  );
}
